import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, ExternalLink, X, Bot, Wallet, Banknote, Link as LinkIcon, Rocket, Coins, Clock, BadgeCheck, Mail } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { Eyebrow } from "./Hero";
import { GET_STARTED_PAGE } from "../mock";

const ICONS = { bot: Bot, wallet: Wallet, banknote: Banknote, link: LinkIcon, rocket: Rocket, coins: Coins, upgrade: BadgeCheck };

function gridClass(count) {
  if (count === 1) return "mx-auto max-w-2xl grid-cols-1";
  if (count === 2) return "sm:grid-cols-2";
  if (count === 4) return "sm:grid-cols-2";
  return "sm:grid-cols-2 lg:grid-cols-3"; // 3 (or more)
}

function VideoTile({ video, onOpen, stepNo, index }) {
  const available = Boolean(video.youtubeId);
  return (
    <button
      type="button"
      onClick={() => available && onOpen(video)}
      disabled={!available}
      data-testid={`gs-step-${stepNo}-video-${index + 1}`}
      className={`group block w-full text-left rounded-xl overflow-hidden card-light ${available ? "btn-lift cursor-pointer" : "cursor-default"}`}
    >
      <div className="relative aspect-video-frame" style={{ background: "linear-gradient(135deg, #201850 0%, #2b2470 60%, #0090CC 160%)" }}>
        {video.thumbnail && (
          <img src={video.thumbnail} alt={video.title} className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,12,40,0.15) 0%, rgba(10,12,40,0.35) 100%)" }} />
        <div className="absolute inset-0 flex items-center justify-center">
          {available ? (
            <span
              className="h-16 w-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
              style={{ background: "rgba(255,255,255,0.95)", boxShadow: "0 10px 30px rgba(0,0,0,0.4)" }}
            >
              <Play className="h-7 w-7 ml-1" style={{ color: "#201850" }} fill="#201850" />
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono-label text-[11px]"
              style={{ background: "rgba(32,24,80,0.7)", color: "#ffffff", border: "1px solid rgba(255,255,255,0.35)" }}
            >
              <Clock className="h-3.5 w-3.5" /> Coming Soon
            </span>
          )}
        </div>
      </div>
      <p className="px-4 py-3 text-base font-semibold" style={{ color: "#201850" }}>{video.title}</p>
    </button>
  );
}

function VideoLightbox({ video, onClose }) {
  useEffect(() => {
    document.body.classList.add("no-scroll");
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="gs-video-lightbox" style={{ alignItems: "center" }}>
      <div className="modal-panel w-full max-w-4xl relative" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          data-testid="gs-video-close"
          className="absolute -top-12 right-0 h-10 w-10 rounded-full flex items-center justify-center transition-colors"
          style={{ background: "rgba(255,255,255,0.9)", color: "#201850" }}
        >
          <X className="h-5 w-5" />
        </button>
        <div className="rounded-xl overflow-hidden" style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}>
          <div className="aspect-video-frame" style={{ background: "#000" }}>
            <iframe data-testid="gs-video-iframe"
              src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
        <p className="mt-3 text-center font-display text-lg" style={{ color: "#ffffff" }}>{video.title}</p>
      </div>
    </div>
  );
}

export default function GetStartedPage() {
  const { eyebrow, heading, sub, email, steps, needHelp } = GET_STARTED_PAGE;
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="page-bg min-h-screen relative overflow-x-hidden">
      <div className="bg-fx" aria-hidden="true" />
      <Header />
      <main className="relative z-10 pt-32 md:pt-36 pb-10">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold mb-8 transition-colors"
            style={{ color: "#0090CC" }}
            data-testid="gs-back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          <div className="text-center">
            <Eyebrow text={eyebrow} testId="gs-eyebrow" />
            <h1 className="eyebrow-title-gap font-display leading-[1.05]" data-testid="gs-heading" style={{ fontSize: "clamp(2rem, 4.5vw, 3.2rem)", color: "#201850" }}>
              {heading}
            </h1>
            <p className="mt-4 max-w-2xl mx-auto text-base md:text-lg" data-testid="gs-description" style={{ color: "rgba(32,24,80,0.7)" }}>{sub}</p>
            <a href={email.href} data-testid="gs-email-assistance" className="setup-email-pill btn-lift mx-auto mt-5 inline-flex max-w-full items-center justify-center gap-2 rounded-full text-sm font-semibold">
              <Mail className="h-4 w-4 shrink-0" /> {email.label}
            </a>
          </div>

          <div className="mt-12 space-y-6">
            {steps.map((step) => {
              const Icon = ICONS[step.icon] || Bot;
              return (
                <div key={step.no} data-testid={`gs-step-${step.no}`} className="rounded-2xl card-light p-6 md:p-8">
                  <div className="flex gap-5 md:gap-6">
                    <div className="shrink-0 flex items-start justify-center pt-1">
                      {step.icon === "bot" ? (
                        <span className="h-20 w-20 rounded-2xl flex items-center justify-center overflow-hidden" style={{ background: "rgba(0,168,232,0.12)" }}>
                          <img src="/eazybot-helmet.png" alt="EazyBot" className="h-[74px] w-[74px] object-contain" />
                        </span>
                      ) : step.icon === "wallet" ? (
                        <div className="flex flex-col items-center gap-2">
                          <img src="/coinbase.png" alt="Coinbase" className="h-20 w-20 rounded-2xl object-cover" style={{ boxShadow: "0 4px 14px rgba(32,24,80,0.2)" }} />
                          <img src="/kraken.jpg" alt="Kraken" className="h-20 w-20 rounded-2xl object-cover" style={{ boxShadow: "0 4px 14px rgba(32,24,80,0.2)" }} />
                        </div>
                      ) : (
                        <span className="h-16 w-16 rounded-2xl flex items-center justify-center" style={{ background: "rgba(0,168,232,0.12)", color: "#0090CC" }}>
                          <Icon className="h-8 w-8" />
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-mono-label text-[11px]" data-testid={`gs-step-${step.no}-number`} style={{ color: "#0090CC" }}>Step {step.no}</p>
                      <h2 className="font-display text-xl md:text-2xl mt-0.5" data-testid={`gs-step-${step.no}-title`} style={{ color: "#201850" }}>{step.title}</h2>
                      {step.noteFirst && step.body2 && (
                        <p className="mt-2 text-sm italic leading-relaxed md:text-base" data-testid={`gs-step-${step.no}-body-2`} style={{ color: "rgba(32,24,80,0.72)" }}>{step.body2}</p>
                      )}
                      {step.body && (
                        <p className="mt-2 text-sm md:text-base leading-relaxed" data-testid={`gs-step-${step.no}-body`} style={{ color: "rgba(32,24,80,0.72)" }}>{step.body}</p>
                      )}
                      {!step.noteFirst && step.body2 && (
                        <p className={`mt-2 text-sm md:text-base leading-relaxed ${step.body2Italic ? "italic" : ""}`} data-testid={`gs-step-${step.no}-body-2`} style={{ color: "rgba(32,24,80,0.72)" }}>{step.body2}</p>
                      )}
                      {step.pill && (
                        <a
                          href={step.pill.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="gs-register-pill"
                          className={`btn-lift ${step.body ? "mt-4" : "mt-3"} inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold`}
                          style={{ background: "linear-gradient(90deg, #00A8E8 0%, #22C3F0 100%)", color: "#201850", boxShadow: "0 4px 14px rgba(0,168,232,0.35)" }}
                        >
                          {step.pill.label}
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>

                  {step.videos && step.videos.length > 0 && (
                    <div className={`mt-6 grid gap-4 ${gridClass(step.videos.length)}`}>
                      {step.videos.map((v, i) => (
                        <VideoTile key={i} video={v} stepNo={step.no} index={i} onOpen={setActiveVideo} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Need Help CTA */}
          <div className="mt-10 rounded-2xl p-8 md:p-10 text-center card-glass-blue" data-testid="gs-need-help">
            <Eyebrow text={needHelp.eyebrow} testId="gs-need-help-eyebrow" />
            <h2 className="eyebrow-title-gap font-display text-2xl md:text-3xl" style={{ color: "#201850" }}>{needHelp.heading}</h2>
            <p className="mt-3 max-w-xl mx-auto text-sm md:text-base" style={{ color: "rgba(32,24,80,0.72)" }}>{needHelp.body}</p>
            <a
              href={needHelp.cta.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gs-community-cta"
              className="btn-lift mt-6 inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold rounded-md"
              style={{ background: "linear-gradient(90deg, #00A8E8 0%, #22C3F0 100%)", color: "#201850" }}
            >
              {needHelp.cta.label}<span aria-hidden>→</span>
            </a>
          </div>
        </div>
      </main>
      <div className="relative z-10">
        <Footer />
      </div>
      {activeVideo && <VideoLightbox video={activeVideo} onClose={() => setActiveVideo(null)} />}
    </div>
  );
}
